import React from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';

export const BFForm = styled.form`
  text-align: left;
  padding: 0 16px;
  width: 400px;
  max-width: 400px;
  margin: 0 auto;

  footer {
    margin-top: 16px;
    font-size: 14px;
    line-height: 20px;
    color: #999;
    text-align: center;
  }

  hr {
    margin: 16px 0;
    border: solid 1px rgba(0, 0, 0, 0.075);
  }
`;

export const BFFormGroup = styled.div`
  margin-bottom: 12px;

  label {
    display: block;
    max-width: 100%;
    font-size: 14px;
    line-height: 1;
    margin-bottom: 8px;
    color: var(--secondary-text-color);
  }
  input[type='text'],
  input[type='password'],
  input[type='email'],
  select {
    display: block;
    width: 100%;
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
    font-size: 15px;
    line-height: 20px;
    padding: 6px 8px;
    border-radius: 4px;
    border: 1px solid #dadada;

    &.has-error {
      border: 1px solid red;
    }
  }

  &.tall {
    input[type='text'],
    input[type='password'],
    input[type='email'],
    select {
      line-height: 42px;
      padding: 6px 16px;
    }
  }

  input[type='checkbox'] {
    margin-right: 8px;
    margin-bottom: 8px;
    min-width: 16px;
    min-height: 16px;
  }

  .form-input-error {
    font-size: 12px;
    line-height: 20px;
    color: red;
  }
`;

export const BFFormSubmit = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  > button {
    margin-left: 8px;
  }

  button.minimal {
    min-width: 0;
    color: var(--primary-text-color);
    &:hover {
      background-color: rgba(var(--primary-fg-rgb), 0.05);
    }
  }
`;

export const isEmptyOtherOption = (value: string): boolean => {
  return value === 'other' || value === 'other/';
};

interface BFFormInputProps {
  type: 'text' | 'password' | 'email' | 'select';
  label?: string;
  value: string | undefined;
  options?: string[];
  allowCustomOption?: boolean;
  onChange: (newValue: string) => void;
  name: string;
  autoComplete?: string;
  placeholder?: string;
  error?: string;
  preventWhiteSpaces?: boolean;
  disabled?: boolean;
  tall?: boolean;
}

export const BFFormInput = forwardRef<HTMLInputElement, BFFormInputProps>(
  (props, ref) => {
    const {
      type,
      label,
      value,
      options,
      allowCustomOption,
      onChange,
      name,
      autoComplete,
      placeholder,
      error,
      preventWhiteSpaces,
      disabled
    } = props;
    const otherInputRef = React.useRef<HTMLInputElement>(null);
    const primarySelectValue = value?.split('/')[0];
    const showOtherOption = primarySelectValue === 'other';

    let otherValue = '';
    if (showOtherOption) {
      const regex = /^other[\/]*(.*)/;
      const match = value?.match(regex);
      if (match && match[1]) {
        otherValue = match[1];
      }
    }

    React.useEffect(() => {
      if (value === 'other' && otherInputRef.current) {
        otherInputRef.current.focus();
      }
    }, [value]);

    const preventSpaces = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        const key = e.nativeEvent.keyCode;
        const keyCodes = [
          32, 160, 5760, 8192, 8192, 8194, 8195, 8196, 8197, 8198, 8199, 8200,
          8201, 8202, 8232, 8233, 8239, 8287, 12288
        ];
        if (keyCodes.some(val => val === key)) {
          e.preventDefault();
        }
      },
      []
    );

    return (
      <BFFormGroup className="tall">
        {label && <label htmlFor={name}>{label}</label>}
        {type === 'select' ? (
          <select
            onChange={e => onChange(e.target.value)}
            className={
              error !== undefined && !showOtherOption ? 'has-error' : undefined
            }
            value={primarySelectValue ?? ''}
            disabled={disabled === true}
          >
            <option disabled={true} value="">
              Select option
            </option>
            {options &&
              options.map(o => (
                <option value={o} key={o}>
                  {o}
                </option>
              ))}
            {allowCustomOption && (
              <option value="other">Other (please specify)</option>
            )}
          </select>
        ) : (
          <input
            ref={ref}
            type={type}
            className={error !== undefined ? 'has-error' : undefined}
            name={name}
            autoComplete={autoComplete ?? name}
            value={value}
            placeholder={placeholder}
            onChange={e => onChange(e.target.value)}
            onKeyPress={preventWhiteSpaces ? preventSpaces : undefined}
            disabled={disabled === true}
          />
        )}
        {showOtherOption && (
          <div style={{ marginTop: 8 }}>
            <input
              ref={otherInputRef}
              type="text"
              className={
                error !== undefined && showOtherOption ? 'has-error' : undefined
              }
              value={otherValue}
              placeholder={placeholder}
              onChange={e => onChange(`other/${e.target.value}`)}
              disabled={disabled === true}
            />
          </div>
        )}
        {error && <div className="form-input-error">{error}</div>}
      </BFFormGroup>
    );
  }
);



// WEBPACK FOOTER //
// ./src/form/BFForm.tsx